import React from "react";
// styles
import cx from "classnames";
import styles from "./Premium.module.scss";
import { PaymentFrequency } from "../../../common/interfaces/enums/PaymentFrequency";
import { IDropdownOption } from "../../../common/interfaces/IDropdownOption";
import Dropdown from "../../../common/components/Forms/Dropdown/Dropdown";
import Tooltip from "../../../common/components/Tooltips/Tooltip";

interface IPremiumProps {
    totalPremium: number;
    size: "small" | "medium" | "large" | "extra-large";
    // Custom styling
    className?: string;
    // Payment Frequency
    paymentFrequency: PaymentFrequency;
    onPaymentFrequencyChange?: (paymentFrequency: PaymentFrequency) => void;
    // Boolean Settings
    hasBlockingError?: boolean;
    isBelowMinimumPremium?: boolean;
    useShortFrequencyLabel?: boolean;
    useFullFrequencyLabel?: boolean;
}

interface IPaymentFrequencyLabels {
    full: string;
    short: string;
}

const paymentFrequencyLabels: {
    [k in PaymentFrequency]: IPaymentFrequencyLabels;
} = {
    Fortnightly: {
        full: "Fortnight",
        short: "Fn"
    },
    Monthly: {
        full: "Month",
        short: "Mn"
    },
    Unknown: {
        full: "Unknown",
        short: "??"
    },
    Weekly: {
        full: "Week",
        short: "Wk"
    },
    Yearly: {
        full: "Year",
        short: "Yr"
    }
};

const validFrequencies: PaymentFrequency[] = [
    PaymentFrequency.Fortnightly,
    PaymentFrequency.Monthly,
    PaymentFrequency.Yearly
];

const getDropdownOption = (type: keyof IPaymentFrequencyLabels): IDropdownOption[] => {
    return validFrequencies.map((freq) => {
        return {
            value: freq,
            label: `/${paymentFrequencyLabels[freq]?.[type]}`
        };
    });
};

const Premium: React.FC<IPremiumProps> = (props) => {
    const {
        totalPremium,
        size,
        className,
        hasBlockingError,
        isBelowMinimumPremium,
        useShortFrequencyLabel,
        useFullFrequencyLabel,
        paymentFrequency,
        onPaymentFrequencyChange
    } = props;

    const hasSetLabelType = useShortFrequencyLabel || useFullFrequencyLabel;

    const priceDollar =
        (totalPremium &&
            // Assuming we round x.xx1 up... (Otherwise toFixed will do swedish(?) rounding)
            // https://stackoverflow.com/questions/11832914/round-to-at-most-2-decimal-places-only-if-necessary
            (Math.round((totalPremium + Number.EPSILON) * 100) / 100)
                ?.toFixed(2)
                .split(".")) ||
        [];

    return (
        <div
            className={cx([
                styles.container,
                {
                    [className]: !!className,
                    [styles["price-small"]]: size === "small",
                    [styles["price-medium"]]: size === "medium",
                    [styles["price-large"]]: size === "large",
                    [styles["price-extra-large"]]: size === "extra-large"
                }
            ])}
            data-total-premium={totalPremium}>
            <div className={cx([styles["price"], styles["price-dollar"], "space-xs"])}>
                {hasBlockingError ? "N/A" : `$${priceDollar[0] || "0"}`}
            </div>
            <div
                className={cx(
                    styles["cents-container"],
                    "layout-flex-column--justify-spaced",
                    "flex-s"
                )}>
                {!hasBlockingError && (
                    <React.Fragment>
                        <span className={cx([styles["price"], styles["price-cents"]])}>
                            .{priceDollar[1] || "00"}
                            {isBelowMinimumPremium && (
                                <span className={cx("text-error")}>*</span>
                            )}
                        </span>
                        {onPaymentFrequencyChange && (
                            <React.Fragment>
                                {/* Full */}
                                <Dropdown
                                    name="paymentFreqPremium"
                                    variant="paymentfreq-full"
                                    selectedOptionId={paymentFrequency}
                                    options={getDropdownOption("full")}
                                    onChange={(i) =>
                                        onPaymentFrequencyChange(
                                            i.target.value as PaymentFrequency
                                        )
                                    }
                                    className={cx({
                                        "layout-hidden-mobile": !hasSetLabelType,
                                        "layout-hidden": useShortFrequencyLabel
                                    })}
                                    noMargin
                                />
                                {/* Truncated */}
                                <Dropdown
                                    name="paymentFreqPremium"
                                    variant="paymentfreq-short"
                                    selectedOptionId={paymentFrequency}
                                    options={getDropdownOption("short")}
                                    onChange={(i) =>
                                        onPaymentFrequencyChange(
                                            i.target.value as PaymentFrequency
                                        )
                                    }
                                    className={cx({
                                        "layout-hidden-desktop": !hasSetLabelType,
                                        "layout-hidden": useFullFrequencyLabel
                                    })}
                                    noMargin
                                />
                            </React.Fragment>
                        )}
                        {!onPaymentFrequencyChange && paymentFrequency && (
                            <span
                                className={
                                    (styles["frequency-text"],
                                    "text-font-size-s",
                                    "text-no-bold")
                                }>
                                {useShortFrequencyLabel &&
                                    `/${paymentFrequencyLabels[paymentFrequency].short}`}
                                {(useFullFrequencyLabel || !hasSetLabelType) &&
                                    `/${paymentFrequencyLabels[paymentFrequency].full}`}
                            </span>
                        )}
                    </React.Fragment>
                )}
            </div>
        </div>
    );
};

export default Premium;
